<script setup lang="ts">
import type { MiniGame, ThemeContent } from '~/stores/challengeWheel'

import ChallengeWheelSetupScreen from '~/components/ChallengeWheel/SetupScreen.vue'

definePageMeta({
  layout: 'games',
  prerender: true,
})

const { t } = useI18n()
const store = useChallengeWheelStore()
const toast = useToast()
const { trackEvent } = useAnalytics()

// ── Setup screen ref (for header menu actions) ─────────
const setupRef = ref<InstanceType<typeof ChallengeWheelSetupScreen> | null>(null)

// ── Header menu actions ────────────────────────────────
function handleReset() {
  setupRef.value?.handleReset()
}

async function handleShare() {
  const url = window.location.href
  if (navigator.share) {
    try {
      await navigator.share({ title: document.title, url })
    } catch {
      // user cancelled or browser blocked — fall through silently
    }
  } else {
    await navigator.clipboard.writeText(url)
    toast.add({ color: 'success', description: t('setupMenu.linkCopied'), duration: 2000, icon: 'mdi:check-circle' })
  }
}

// ── Load all content at once ──────────────────────────────
const [ { data: allContent }, { data: ui } ] = await Promise.all([
  useAsyncData(
    'challenge-wheel-content',
    () => queryCollection('challengeWheel').all(),
  ),
  useChallengeWheelUI(),
])

useSeoMeta({
  description: ui.value?.page.description,
  ogDescription: ui.value?.page.description,
  title: ui.value?.page.title,
})

function getThemeContent(): null | ThemeContent {
  const found = allContent.value?.find(c => c.theme === store.config.theme)
  return found as null | ThemeContent
}

// ── Game reset hook ────────────────────────────────────
const { register: registerReset } = useGameReset()
registerReset(() => {
  if (import.meta.client && document.fullscreenElement) {
    document.exitFullscreen().catch(() => {})
  }
  store.exitGame()
})

// ── Game lifecycle ───────────────────────────────────────
function handleStart() {
  const content = getThemeContent()
  if (content) {
    store.setContent(content)
  }
  // Inject mini-game labels from content before the wheel is built.
  if (ui.value) {
    const labels = Object.fromEntries(
      ui.value.setup.miniGames.games.map(g => [ g.value, g.label ]),
    ) as Record<MiniGame, string>
    store.setMiniGameLabels(labels)
  }
  // Must request fullscreen synchronously within the user-gesture context.
  // Skip in dev mode so the browser toolbar stays accessible during development.
  if (import.meta.client && !import.meta.dev) {
    document.documentElement.requestFullscreen().catch(() => {})
  }
  store.startGame()
  trackEvent('game_started', {
    game: 'challenge-wheel',
    selected_games: store.config.selectedGames,
    team_count: store.teams.length,
    team_structure: store.config.teamStructure,
    theme: store.config.theme,
  })
}
</script>

<template>
  <!-- Setup screen -->
  <div
    v-if="store.phase === 'setup'"
    class="flex min-h-screen items-center justify-center p-6"
  >
    <div class="w-full max-w-3xl">
      <UCard :ui="{ root: 'shadow-xl ring', header: 'bg-muted/75', body: 'bg-muted/5', footer: 'bg-muted/20' }">
        <!-- Card header: icon + title + description + menu -->
        <template #header>
          <div class="flex items-center gap-4 py-1">
            <UIcon
              class="size-12 shrink-0"
              name="sfg:challenge-wheel"
            />
            <div class="min-w-0 flex-1">
              <h1 class="text-2xl font-bold">
                {{ ui?.page.title }}
              </h1>
              <p class="mt-0.5 text-sm text-muted">
                {{ ui?.page.description }}
              </p>
            </div>
            <UDropdownMenu
              :items="[
                [{ icon: 'mdi:share-variant-outline', label: t('setupMenu.shareGame'), onSelect: handleShare }],
                [{ icon: 'mdi:refresh', label: t('setupMenu.resetSettings'), onSelect: handleReset }],
              ]"
              :content="{ side: 'bottom', align: 'end', sideOffset: 8 }"
              :modal="false"
              :ui="{ content: 'w-44' }"
            >
              <UButton
                color="neutral"
                icon="mdi:dots-vertical"
                size="sm"
                variant="ghost"
                :aria-label="t('setupMenu.menuAriaLabel')"
              />
            </UDropdownMenu>
          </div>
        </template>

        <template #footer>
          <div class="w-full flex flex-col items-center gap-1 sm:flex-row sm:justify-center">
            <NuxtLink
              class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-primary hover:text-primary/80"
              to="/"
            >
              <UIcon
                class="size-4"
                name="mdi:fire"
              />
              {{ t('site.name') }}
            </NuxtLink>
            <span class="hidden text-muted sm:inline">&mdash;</span>
            <span class="text-center text-muted">{{ t('site.tagline') }}</span>
          </div>
        </template>

        <!-- Setup form -->
        <ClientOnly>
          <ChallengeWheelSetupScreen ref="setupRef" @start="handleStart"/>
          <template #fallback>
            <div class="py-16 text-center text-muted">
              {{ ui?.setup.loading }}
            </div>
          </template>
        </ClientOnly>
      </UCard>
    </div>
  </div>

  <!-- Game screen — fullscreen, no site chrome -->
  <ChallengeWheelGameScreen v-else/>
</template>
